import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { IRelatedAsset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { IRelatedEntity } from '@app/core/model/entities/entity';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { AbstractFieldBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { eq, textContains } from '@app/shared/extra/filter-model-builder';
import { doOnFirst, getLocale, getValue } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { exhaustMap, merge, Observable, pipe, Subject, takeWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

export interface SuggestedAsset {
  asset: RelatedAsset;
  checked: boolean;
}

@Component({
  selector: 'assets-chips-field-builder',
  templateUrl: './assets-chips-field-builder.component.html',
  styleUrls: ['../chips-field-builder.component.scss', './entity-chips-field-builder.scss'],
  standalone: false
})
export class AssetsChipsFieldBuilderComponent extends AbstractFieldBuilder<FormControl<string>> implements OnInit, AfterViewInit {
  private readonly assetsService = inject(AssetsService);
  private readonly fb = inject(FormBuilder);

  @ViewChild('inputField') public declare inputField: ElementRef<HTMLInputElement>;
  @ViewChild('suggestions') public declare suggestions: MatAutocomplete;
  @ViewChild('trigger') public trigger: MatAutocompleteTrigger;

  // Current entity assets, avoid getValue call in the template (bad for performance)
  protected assetValues: RelatedAsset[] = [];
  public allSelected = false;
  public hasData: boolean = false;
  public assetOptions$: Observable<SuggestedAsset[]>;
  private suggestedAssets: Map<string, SuggestedAsset> = new Map();
  public selectedEntities: IRelatedEntity[] = [];
  protected viewportHeight: string = '0px';

  private scrollTrigger$ = new Subject<number>();

  public asyncPreconditionsForEdition = false;
  protected selectionChangedSubject = new Subject<void>();
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  /**
   * Load assets data. Then, create the form, and add the chips.
   */
  public ngOnInit(): void {
    // Initialise form
    this.assetValues = getValue(this.entity, this.fieldConfig.fieldPath)
      ?.map((asset: IRelatedAsset) => new RelatedAsset(asset))
      // Put accessible assets first
      ?.sort((a: RelatedAsset, _: RelatedAsset) => {
        return this.accessManager.hasAccessToRelatedAsset(a, this.appManager.currentOrganization.id) ? -1 : 1;
      }) ?? [];
    this.form = this.fb.group({field: this.fb.control('')});
    this.form.addControl('selectedAssetIds', this.fb.control(
      this.assetValues.map(asset => asset.id),
      this.fieldConfig.customOptions?.required ? Validators.required : null
    ));

    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);
    this.getNextState();

    // Reusable pipeline
    const reloadNewPage = pipe(
      debounceTime(500),
      filter(() => this.currentMode === FieldStateMode.EDIT || this.isCreatingEntity),
      tap(() => this.suggestedAssets.clear()),
      switchMap(() => this.scrollTrigger$.pipe(
          startWith(0),
          distinctUntilChanged(),
          // Reject scroll indices until the threshold is reached
          filter(index => index >= this.suggestedAssets.size - this.appConfig.PRELOAD_THRESHOLD),
          // Ignore all new inputs until the api call completes
          exhaustMap(() => this.loadAssetsNextPage(this.suggestedAssets.size)),
          // Only initialise hasData after loading the first page
          doOnFirst(assetsFirstPage => this.hasData = assetsFirstPage.length > 0),
          // Emit the accumulation of existing data and the new page
          tap(assetsNewPage => this.setSuggestedAssets([...Array.from(this.suggestedAssets.values()), ...assetsNewPage])),
          // Unsubscribe once the final page is received
          takeWhile(projectsNewPage => projectsNewPage.length === this.appConfig.PREFERRED_REQUEST_PAGE_SIZE, true)
        )
      )
    );

    // Load Assets suggestions when necessary
    this.asyncPreconditionsForEdition$
      .pipe(
        tap(preconditionsToEdit => {
          this.asyncPreconditionsForEdition = preconditionsToEdit;
          if (!preconditionsToEdit) {
            this.form.controls.field.disable();
            this.form.controls.selectedAssetIds.disable();
          }
        }),
        filter(preconditionsToEdit => preconditionsToEdit && this.preconditionsForEdition),
        // For each change in input, load a new first page of assets to match the new displayName filter
        switchMap(() => {
          return merge(
            this.form.controls.field.valueChanges.pipe(
              startWith(''),
              reloadNewPage
            ),
            this.getFieldGroupState().pipe(
              filter(newMode => newMode === FieldStateMode.EDIT),
              reloadNewPage
            )
          );
        }),
        tap(() => {
          let loadedAssetsCount = this.suggestedAssets.size;
          if (this.hasData) {
            // Take into account the select all option in the height calculation
            loadedAssetsCount++;
          }
          const count = Math.min(loadedAssetsCount, this.appConfig.CDK_VIEWPORT_MAXIMUM_ITEM_COUNT);
          this.viewportHeight = `${count * this.appConfig.CDK_VIEWPORT_ITEM_HEIGHT}px`;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.assetOptions$ = this.selectionChangedSubject.asObservable().pipe(
      map(() => {
        this.setFieldValue(this.selectedEntities);
        this.form.controls.selectedAssetIds.setValue(this.selectedEntities.map(asset => asset.id));

        return Array.from(this.suggestedAssets.values());
      })
    );
  }

  /**
   * Set up hooks and reset form when field group state is on error.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();
    this.getFieldGroupState().pipe(
      filter(state => state === FieldStateMode.ERROR),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.cancel();
    });
  }

  /**
   * Either activate the field into editing mode or save it if you were editing it
   */
  protected activate(): void {
    if (this.currentMode === this.FieldMode.EDIT && this.form.valid) {
      this.save();
    } else if (this.accessManager.hasAllNeededPermissions(this.permissionsForEdition) && this.preconditionsForEdition && this.asyncPreconditionsForEdition) {
      this.edit();
    }
  }

  /**
   * Build Select Params with filters on typeModules and displayName according to the current form.
   * @return SelectParams
   */
  public buildSelectParams(): SelectParams {
    // Build filters
    const filters: Filter[] = [];

    if (this.sectionEntityType) {
      filters.push(eq('computedProperties.typeModules', this.sectionEntityType));
    }

    if (typeof this.form.controls.field.value === 'string') {
      filters.push(textContains('computedProperties.displayName', this.form.controls.field.value));
    }

    // Build Select Params
    return {
      locale: getLocale(),
      sorts: [
        {
          field: 'lastChangeDate',
          direction: 'DESC'
        }
      ],
      filters,
      count: this.appConfig.PREFERRED_REQUEST_PAGE_SIZE
    };
  }

  /**
   * Load assets next page.
   * @param startingIndex Page to load. NB: First page is 0.
   * @return Observable<SuggestedAsset[]> A list of SuggestedAsset matching the form filters.
   */
  public loadAssetsNextPage(startingIndex: number): Observable<SuggestedAsset[]> {
    const params = {...this.buildSelectParams(), start: startingIndex};
    return this.assetsService.loadAssetsPage(params, false)
      .pipe(
        map(assetsPage =>
          assetsPage.map(asset => {
            return {
              asset: asset.toRelatedAsset(),
              checked: this.selectedEntities.map(e => e.id).includes(asset.id)
            };
          })
        )
      );
  }

  /**
   * Set a new map of suggested Assets and update allSelected flags as well as selectedAssetIds in form.
   * @param assets List of assets to set as the new suggestions list.
   */
  public setSuggestedAssets(assets: SuggestedAsset[]): void {
    this.suggestedAssets = new Map(assets.map(suggestedAsset => [
      suggestedAsset.asset.id,
      suggestedAsset
    ]));

    // Set allSelected flag (it can be true only if all assets have been loaded)
    const hasNextPage = assets.length % this.appConfig.PREFERRED_REQUEST_PAGE_SIZE === 0;
    this.allSelected = !hasNextPage &&
      Array.from(this.suggestedAssets.values())
        .every(suggestedAsset => !!suggestedAsset.checked);

    this.selectionChangedSubject.next();
  }

  /**
   * Emit the index of the top item displayed in the viewport.
   * @param index Scroll index.
   */
  public onScroll(index: number) {
    this.scrollTrigger$.next(index);
  }

  /**
   * Resets the field by clearing the value.
   */
  protected doReset(): void {
    // Lets the form reset itself to its initial values
    this.form.reset();
  }

  /**
   * Get the field value to display in read mode.
   * @return any
   */
  public getFieldValue(): string[] {
    return getValue(this.entity, this.fieldConfig.fieldPath)
        ?.map((asset: IRelatedAsset) => asset.id)
      ?? [];
  }

  /**
   * Recreate the chips when switching to edit mode.
   */
  public edit(): void {
    AbstractFieldBuilder.prototype.edit.call(this);
    if (this.selectedEntities.length === 0) {
      this.selectedEntities.push(...(this.assetValues ?? []));
    }
  }

  /**
   * Add or remove an asset to the selection.
   * @param asset Asset to select or deselect.
   * @protected
   */
  protected selectToggle(asset: IRelatedEntity): void {
    const isChecked = !this.selectedEntities.some(selectedAsset => selectedAsset.id === asset.id);

    if (isChecked) {
      this.selectedEntities.push(asset);
    } else {
      this.selectedEntities = this.selectedEntities.filter(selectedAsset => selectedAsset.id !== asset.id);
    }

    if (this.suggestedAssets.has(asset.id)) {
      this.suggestedAssets.set(asset.id, {asset: asset as RelatedAsset, checked: isChecked});
    }
    this.allSelected = Array.from(this.suggestedAssets.values()).every(suggestedAsset => !!suggestedAsset.checked);
    this.selectionChangedSubject.next();
  }

  /**
   * Select or deselect all filtered assets when the user clicks on selectAll checkbox.
   * @protected
   */
  protected selectAllToggle(): void {
    this.allSelected = !this.allSelected;

    // If all suggestions are to be selected, make sure all pages were loaded. Otherwise, no need to load more pages.
    if (this.allSelected) {
      this.assetsService.loadAllAssets(this.buildSelectParams(), false)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(assetsPages => {
            const selectedEntitiesIds = new Set(this.selectedEntities.map(a => a.id));
            const suggestedAssets: SuggestedAsset[] = [];

            assetsPages.forEach(asset => {
              if (!selectedEntitiesIds.has(asset.id)) {
                this.selectedEntities.push(asset);
                selectedEntitiesIds.add(asset.id);
              }
              suggestedAssets.push({asset: asset.toRelatedAsset(), checked: true});
            });

            this.suggestedAssets = new Map(suggestedAssets.map(suggestedAsset => [
              suggestedAsset.asset.id,
              suggestedAsset
            ]));
            this.selectionChangedSubject.next();
          }
        );
    } else {
      this.selectedEntities = this.selectedEntities.filter(asset => !this.suggestedAssets.has(asset.id));
      for (const [key, value] of this.suggestedAssets) {
        this.suggestedAssets.set(key, {...value, checked: false});
      }

      this.selectionChangedSubject.next();
    }
  }

  /**
   * Navigate to the Dashboard of the related Asset
   * @param assetId
   */
  public async navigateToAssetDashboard(assetId: string): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      assetId
    );
    await this.assetsService.navigateToAssetSheet(assetId, 'dashboard');
  }

  /**
   * Keep the autocomplete opened after each item is picked.
   */
  public keepPanelOpen(): void {
    requestAnimationFrame(() => {
      this.trigger.openPanel();
      this.inputField.nativeElement.focus();
    });
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => this.inputField?.nativeElement?.focus());
    }
  }

  /**
   * Stop event propagation and call selectToggle method when a mat-option is clicked.
   */
  public optionClicked(event: MouseEvent, option?: SuggestedAsset): void {
    event.stopPropagation();

    if (!!option) {
      this.selectToggle(option.asset);
    } else {
      this.selectAllToggle();
    }
  }

  /**
   * Reset the form and the chips to their initial state, then switch to read mode.
   */
  public cancel(): void {
    this.selectedEntities = this.isCreatingEntity ? [] : this.assetValues;
    for (const [key, value] of this.suggestedAssets) {
      this.suggestedAssets.set(key, {asset: value.asset, checked: this.fieldInitValue.includes(key)});
    }
    this.allSelected = Array.from(this.suggestedAssets.values()).every(suggestedAsset => !!suggestedAsset.checked);
    this.selectionChangedSubject.next();

    this.form.controls.field.setValue('');
    this.setFieldValue(this.fieldInitValue);
    this.getNextState();
  }
}
